/**
 * «Cambió el plano» (OPUS ci, idea 4), IFC: arma la comparación de dos revisiones de un modelo con la hoja del
 * plano. Sin IA, $0, puro (lo que mide viene de afuera: `MedicionDeModelo` de cada revisión).
 *
 *  - Piezas: una por instancia del inventario (id = expressID, gid = GlobalId, grupo = la etiqueta del tipo,
 *    clase = la clase IFC, nivel = el nombre del piso en el modelo, centro y medidas de la pieza).
 *  - Medir un elemento (`medirA`/`medirB`): la MISMA foto que la pantalla del plano (`fotoDeElemento`), con el
 *    contexto de CADA revisión. El elemento se arma con `asignablesDelPlano`: las piezas que no están en sus `ids`
 *    se excluyen, igual que cuando quien cotiza las saca en el visor.
 *  - Magnitudes (complementarios): `magnitudesDelPrincipal` con los ajustes con que se midió el principal.
 *
 * La regla de qué cambia y qué se aplica solo vive en @supernova/shared (`compararRevisiones`).
 */
import {
  compararRevisiones,
  elementosDeSeleccion,
  type ComoSeMidio,
  type CambioDePieza,
  type ComparacionDePlanos,
  type FotoGenerador,
  type ImpactoDeRenglon,
  type MagnitudesPrincipal,
  type ParametrosCambioDePlano,
  type PiezaComparable,
  type PlanoCapa,
  type PlanoRenglon,
  type PlanoSeleccion,
  type RenglonParaImpacto,
  type TipoCambioPieza,
} from '@supernova/shared';
import {
  asignablesDelPlano,
  cuentaDeVanosDe,
  decisionIfc,
  fotoDeElemento,
  largoConVanosDe,
  magnitudesDelPrincipal,
  type Asignable,
  type ContextoAsignables,
  type ContextoFoto,
} from './medirElemento.js';
import { inventarioDeMedicion, type MedicionDeModelo } from './medirModeloIfc.js';
import { etiquetasDeNiveles } from './nivelesIfc.js';

type UnElemento = Omit<PlanoSeleccion, 'ademas'>;

/** Medir una revisión: la foto y las magnitudes de UN elemento, con los ajustes con que se midió el renglón. */
export interface MedidorDeRevision {
  medir: (sel: UnElemento, como: ComoSeMidio) => FotoGenerador | null;
  magnitudes: (sel: UnElemento, como: ComoSeMidio) => MagnitudesPrincipal | null;
}

/** El medidor de UNA revisión: los contextos de `asignablesDelPlano` y `fotoDeElemento` armados con su inventario. */
export function medidorDeRevision(m: MedicionDeModelo): MedidorDeRevision {
  const inventario = inventarioDeMedicion(m);
  const ctxAsignables: ContextoAsignables = {
    inventario,
    factorEscala: 1,
    capasClasificadas: null,
    // Las correcciones «es solo dibujo / se construye» solo deciden si un tipo se puede asignar, no cuánto mide.
    decisionOverride: {},
    alturaPorVista: new Map(),
    escalaSinResolver: false,
  };
  const piezaDelModelo: ContextoFoto['piezaDelModelo'] = new Map();
  for (const c of m.capas) for (const x of c.instancias ?? []) piezaDelModelo.set(x.id, { clase: c.categoria ?? null, dims: x.dims });
  const ctxFoto: ContextoFoto = {
    inventario,
    factorEscala: 1,
    capasClasificadas: null,
    decisionOverride: {},
    nivelDeElemento: m.nivelDeElemento,
    etiquetaNivel: etiquetasDeNiveles(m.niveles),
    piezaDelModelo,
  };
  const porClave = new Map(m.capas.map((c) => [c.capa, c] as const));

  /** El elemento como lo arma la pantalla; null si en esta revisión no hay ese tipo medido. */
  const asignable = (sel: UnElemento): Asignable | null => {
    if (sel.tipo !== 'capa') return null; // un IFC no tiene bloques
    const info = porClave.get(sel.clave);
    if (!info?.medidas) return null;
    const excluidas = new Set<number>();
    if (sel.ids) {
      const incluidas = new Set(sel.ids);
      for (const x of info.instancias ?? []) if (!incluidas.has(String(x.id))) excluidas.add(x.id);
    }
    return asignablesDelPlano(ctxAsignables, new Set([sel.clave]), new Set(), excluidas, null)[0] ?? null;
  };

  return {
    medir: (sel, como) => {
      const a = asignable(sel);
      if (!a) return null;
      return fotoDeElemento(ctxFoto, a, {
        magnitud: como.magnitud,
        unidadConcepto: como.unidad,
        altoStr: como.altoStr,
        espesorStr: como.espesorStr,
        altosVanos: como.altosVanos,
      });
    },
    magnitudes: (sel, como) => {
      const a = asignable(sel);
      if (!a) return null;
      return magnitudesDelPrincipal(a, {
        altoStr: como.altoStr,
        espesorStr: como.espesorStr,
        cuentaVanos: cuentaDeVanosDe(a.muroVanos, como.altoStr, como.altosVanos),
        largoConVanosM: largoConVanosDe(a.muroVanos),
        capas: inventario.capas,
      });
    },
  };
}

/**
 * Las piezas de una revisión, listas para compararse. Entran los tipos que se construyen (la regla de la
 * categoría IFC) y, aunque no, los de las clases que la hoja usa: si la hoja cobra algo que por default es
 * «solo dibujo», sus piezas tienen que estar en los dos lados para seguirlas.
 */
export function piezasDeMedicion(m: MedicionDeModelo, clasesDeLaHoja: ReadonlySet<string> = new Set()): PiezaComparable[] {
  const nombreNivel = new Map(m.niveles.map((n) => [n.id, n.nombre] as const));
  const out: PiezaComparable[] = [];
  for (const c of m.capas) {
    const clase = c.categoria ?? '';
    if (decisionIfc(c.categoria, c.ifc?.supertipos) === 'ruido' && !clasesDeLaHoja.has(clase)) continue;
    for (const x of c.instancias ?? []) out.push(piezaDeInstancia(c, x, m.nivelDeElemento, nombreNivel));
  }
  return out;
}

function piezaDeInstancia(
  c: PlanoCapa,
  x: NonNullable<PlanoCapa['instancias']>[number],
  nivelDeElemento: ReadonlyMap<number, number>,
  nombreNivel: ReadonlyMap<number, string>,
): PiezaComparable {
  const nivel = nivelDeElemento.get(x.id);
  const medidas: PiezaComparable['medidas'] = {};
  if (x.longitud != null) medidas.longitud = x.longitud;
  if (x.area != null) medidas.area = x.area;
  if (x.volumen != null) medidas.volumen = x.volumen;
  if (x.peso != null) medidas.peso = x.peso;
  return {
    id: String(x.id),
    gid: x.gid ?? null,
    grupo: c.capa,
    clase: c.categoria ?? '',
    nivel: nivel != null ? nombreNivel.get(nivel) ?? null : null,
    ...(x.nombre ? { nombre: x.nombre } : {}),
    centro: x.centro ?? null,
    medidas,
  };
}

/** Lo que la comparación necesita de cada renglón de la hoja. */
export function renglonParaImpacto(r: PlanoRenglon): RenglonParaImpacto {
  return {
    id: r.id,
    principalId: r.principalId ?? null,
    padres: r.padres ?? [],
    unidad: r.unidad,
    cantidad: r.cantidad,
    cantidadMedida: r.cantidadMedida ?? null,
    precioUnitario: r.precioUnitario,
    importe: r.importe,
    apuIaCostoDirecto: r.apuIaCostoDirecto ?? null,
    apuIaImporte: r.apuIaImporte ?? null,
    seleccion: r.seleccion,
    generador: r.generador ?? null,
    magnitudConcepto: r.magnitudConcepto ?? null,
  };
}

/** «13 de octubre de 2026». */
export function fechaDeRevision(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

export interface EntradaCompararIfc {
  a: MedicionDeModelo;
  b: MedicionDeModelo;
  renglones: readonly PlanoRenglon[];
  parametros: ParametrosCambioDePlano;
  /** Fecha de la revisión nueva, ya escrita («13 de octubre de 2026»). */
  fechaRevision: string;
}

/** Compara dos revisiones de un modelo IFC con la hoja del plano (vista previa; el servidor la valida al aplicar). */
export function compararRevisionIfc(e: EntradaCompararIfc): ComparacionDePlanos {
  const medA = medidorDeRevision(e.a);
  const medB = medidorDeRevision(e.b);
  // Las clases de los tipos que la hoja usa (en A): sus piezas se siguen aunque por default sean «solo dibujo».
  const claseDeA = new Map(e.a.capas.map((c) => [c.capa, c.categoria ?? ''] as const));
  const clases = new Set<string>();
  for (const r of e.renglones) {
    for (const x of elementosDeSeleccion(r.seleccion)) {
      const k = claseDeA.get(x.clave);
      if (k != null) clases.add(k);
    }
  }
  return compararRevisiones({
    formato: 'ifc',
    parametros: e.parametros,
    piezasA: piezasDeMedicion(e.a, clases),
    piezasB: piezasDeMedicion(e.b, clases),
    renglones: e.renglones.map(renglonParaImpacto),
    medirA: medA.medir,
    medirB: medB.medir,
    magnitudesA: medA.magnitudes,
    magnitudesB: medB.magnitudes,
    fechaRevision: e.fechaRevision,
  });
}

// ---------------------------------------------------------------------------------------------------
// Para la pantalla
// ---------------------------------------------------------------------------------------------------

/** Lo que un renglón suma a la hoja: el importe de catálogo o, si no, el de su análisis con IA (igual que T1). */
export function importeDeHoja(importe: number | null, apuIa: number | null): number {
  return importe ?? apuIa ?? 0;
}

/** El importe con el que queda el renglón si se aplica la revisión (lo que no se aplica solo, con el de hoy). */
export function importeDespues(i: ImpactoDeRenglon): number {
  return i.cantidadSeAplicaSola ? importeDeHoja(i.importeNuevo, i.apuIaImporteNuevo) : importeDeHoja(i.importeAntes, i.apuIaImporteAntes);
}

/** Qué hace «Aplicar a mi hoja», contado (lo dice la confirmación). */
export interface ResumenDeAplicar {
  /** Renglones cuya cantidad cambia sola. */
  cantidades: number;
  /** Renglones cuya selección se actualiza (las piezas que cubre en el plano nuevo). */
  selecciones: number;
  /** Renglones que quedan marcados para revisar (su cantidad no cambia). */
  revisar: number;
  /** Renglones con la cantidad corregida a mano que el plano nuevo mide distinto. */
  aMano: number;
  /** Renglones que ya no están en el plano nuevo (quedan en 0). */
  yaNoEstan: number;
}

export function resumenDeAplicar(c: ComparacionDePlanos): ResumenDeAplicar {
  let cantidades = 0;
  let selecciones = 0;
  let revisar = 0;
  let aMano = 0;
  let yaNoEstan = 0;
  for (const r of c.renglones) {
    if (r.cantidadSeAplicaSola && r.estado === 'cambia') cantidades++;
    if (r.cantidadSeAplicaSola && r.estado === 'ya_no_esta') yaNoEstan++;
    if (r.seleccionSeAplicaSola && r.seleccionNueva) selecciones++;
    if (r.estado === 'revisar') revisar++;
    if (r.aMano && r.cantidadNueva != null && r.estado === 'revisar') aMano++;
  }
  return { cantidades, selecciones, revisar, aMano, yaNoEstan };
}

/** Orden de la lista: primero lo que cambia, luego lo que hay que revisar, al final lo que no cambia. */
const ORDEN_ESTADO: Record<ImpactoDeRenglon['estado'], number> = { cambia: 0, ya_no_esta: 1, revisar: 2, igual: 3, no_se_recalcula: 4 };
export function ordenarImpacto(rs: readonly ImpactoDeRenglon[]): ImpactoDeRenglon[] {
  return rs.map((r, i) => ({ r, i })).sort((x, y) => ORDEN_ESTADO[x.r.estado] - ORDEN_ESTADO[y.r.estado] || x.i - y.i).map((x) => x.r);
}

// ---------------------------------------------------------------------------------------------------
// Vista previa en 3D: qué pieza se pinta de qué color en «Antes» (el modelo con el que armaste la hoja) y en «Ahora»
// ---------------------------------------------------------------------------------------------------

/** El tono de estado (los del tema) con que se pinta un tipo de cambio; los mismos que las pastillas de la lista. */
export type TonoDeCambio = 'ok' | 'bad' | 'warn' | 'accent';
export const TONO_DE_CAMBIO: Readonly<Record<TipoCambioPieza, TonoDeCambio>> = {
  agregada: 'ok',
  quitada: 'bad',
  cambio_medida: 'warn',
  cambio_tipo: 'accent',
  movida: 'accent',
};

/** Qué lado de un cambio existe en cada vista: lo quitado solo está en A («Antes»), lo agregado solo en B («Ahora»). */
const ladoDe = (p: CambioDePieza, vista: 'a' | 'b') => (vista === 'a' ? p.a : p.b);

/**
 * expressID → tono para el visor de la vista dada. Cada vista usa el id de SU modelo (el de A no es el de B). Las
 * piezas sin id numérico se saltan. Una pareja dudosa conserva su tono (la lista ya la marca «no es seguro»).
 */
export function coloresDeLaVista(piezas: readonly CambioDePieza[], vista: 'a' | 'b'): Map<number, TonoDeCambio> {
  const out = new Map<number, TonoDeCambio>();
  for (const p of piezas) {
    const lado = ladoDe(p, vista);
    if (!lado) continue;
    const eid = Number(lado.id);
    if (Number.isFinite(eid)) out.set(eid, TONO_DE_CAMBIO[p.tipo]);
  }
  return out;
}

/** La fila de la lista de cambios que corresponde a una pieza del visor (en la vista dada); -1 si ninguna. */
export function filaDePieza(piezas: readonly CambioDePieza[], vista: 'a' | 'b', eid: number): number {
  return piezas.findIndex((p) => {
    const lado = ladoDe(p, vista);
    return lado != null && Number(lado.id) === eid;
  });
}

/** El expressID de la pieza de una fila en la vista dada; null si en esa vista no existe (una nueva vista en «Antes»). */
export function piezaDeFila(p: CambioDePieza, vista: 'a' | 'b'): number | null {
  const lado = ladoDe(p, vista);
  const eid = lado ? Number(lado.id) : NaN;
  return Number.isFinite(eid) ? eid : null;
}
